import React, { useEffect, useMemo, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getLaborUtilizationSummaryAPI } from "../../../../api/reports-api";
import PaginationBar, { usePagination, PAGE_SIZE_OPTIONS } from "./Pagination";
import { useLuTableSortFilter, LuSortFilterTh } from "./tableSortFilterUtils";

const MONTH_KEYS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function monthsTotal(monthsObj) {
  return MONTH_KEYS.reduce((sum, k) => sum + (monthsObj?.[k] ?? 0), 0);
}

function buildCols(MONTHS) {
  return [
    { key: "empId", label: "Emp Id", isText: true },
    { key: "empName", label: "Emp Name", isText: true },
    { key: "empStatus", label: "Emp Status", isText: true },
    ...MONTHS.map((m) => ({
      key: m.key,
      label: m.label,
      isText: false,
      getValue: (row) => row.months?.[m.key],
    })),
    {
      key: "total",
      label: "Total",
      isText: false,
      getValue: (row) => row.total ?? monthsTotal(row.months),
    },
  ];
}

function recomputeTotals(rows) {
  const months = {};
  MONTH_KEYS.forEach((k) => {
    months[k] = rows.reduce((s, r) => s + (Number(r.months?.[k]) || 0), 0);
  });
  return {
    empName: "Total",
    months,
    total: rows.reduce((s, r) => s + (Number(r.total ?? monthsTotal(r.months)) || 0), 0),
  };
}

export default function SummaryTab({ classes, MONTHS, fmtHours, negStyle, filters, onMeta }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [actualThru, setActualThru] = useState(0);

  const cols = useMemo(() => buildCols(MONTHS), [MONTHS]);
  const {
    sortKeys,
    sortedRows,
    hasTableFilters,
    openFilterKey,
    onThClick,
    getSortColumnLabel,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
    renderFilterPanel,
  } = useLuTableSortFilter(cols, rows);

  const { pagedItems: pagedRows, resetPage, ...pagination } = usePagination(
    sortedRows,
    PAGE_SIZE_OPTIONS[0],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLaborUtilizationSummaryAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setRows(d.rows || []);
          setTotals(d.totals || null);
          setActualThru(Number(d.actualThroughMonth) || 0);
          resetPage();
          onMeta?.({ actualThroughMonth: d.actualThroughMonth, fiscalYear: d.fiscalYear });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayTotals = useMemo(() => {
    if (!hasTableFilters) return totals;
    return recomputeTotals(sortedRows);
  }, [hasTableFilters, sortedRows, totals]);

  if (loading) return <Loading />;

  const thProps = {
    sortKeys,
    onThClick,
    getSortColumnLabel,
    openFilterKey,
    hasFilterValue,
    toggleFilter,
    filterBtnRefs,
  };

  return (
    <div>
      <div className={classes.tableWrapScrollable}>
        <table className={classes.table}>
          <thead style={{ position: "sticky", top: 0, zIndex: 3, boxShadow: "0 2px 0 #E9EDF3" }}>
            <tr>
              <LuSortFilterTh col={cols[0]} className={classes.thEmpId} {...thProps} />
              <LuSortFilterTh col={cols[1]} className={classes.thEmpName} {...thProps} />
              <LuSortFilterTh col={cols[2]} className={classes.thEmpStatus} {...thProps} align="center" />
              {MONTHS.map((m, i) => {
                const isActual = actualThru > 0 && i + 1 <= actualThru;
                return (
                  <LuSortFilterTh
                    key={m.key}
                    col={cols.find((c) => c.key === m.key)}
                    className={isActual ? classes.thMonthActual : classes.thMonth}
                    {...thProps}
                    align="right"
                    isActual={isActual}
                  />
                );
              })}
              <LuSortFilterTh
                col={cols[cols.length - 1]}
                className={classes.thTotal}
                {...thProps}
                align="right"
              />
            </tr>
          </thead>
          <tbody>
            {displayTotals && (
              <tr>
                <td className={classes.tdTotalLabel}>{displayTotals.empName || "Total"}</td>
                <td className={classes.tdTotalEmpty} style={{ position: "sticky", left: 80, zIndex: 1 }} />
                <td className={classes.tdTotalEmpty} style={{ position: "sticky", left: 280, zIndex: 1 }} />
                {MONTHS.map((m) => (
                  <td key={m.key} className={classes.tdTotalMonth} style={negStyle?.(displayTotals.months?.[m.key])}>
                    {fmtHours(displayTotals.months?.[m.key])}
                  </td>
                ))}
                <td className={classes.tdTotalColMain} style={negStyle?.(displayTotals.total ?? monthsTotal(displayTotals.months))}>
                  {fmtHours(displayTotals.total ?? monthsTotal(displayTotals.months))}
                </td>
              </tr>
            )}
            {pagedRows.map((emp) => (
              <tr key={emp.empId}>
                <td className={classes.tdEmpId}>{emp.empId}</td>
                <td className={classes.tdEmpName}>{emp.empName}</td>
                <td className={classes.tdEmpStatus}>{emp.empStatus}</td>
                {MONTHS.map((m) => (
                  <td key={m.key} className={classes.tdMonth} style={negStyle?.(emp.months?.[m.key])}>
                    {fmtHours(emp.months?.[m.key])}
                  </td>
                ))}
                <td className={classes.tdEmpTotal} style={negStyle?.(emp.total ?? monthsTotal(emp.months))}>
                  {fmtHours(emp.total ?? monthsTotal(emp.months))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar
        {...pagination}
        totalItems={sortedRows.length}
        onPageChange={pagination.goToPage}
        onPageSizeChange={pagination.handlePageSizeChange}
      />
      {renderFilterPanel()}
    </div>
  );
}
